import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { AiKeyApiProvider, useAiKeyApi } from '../Services/AiKeyApiProvider';
import { AiKeyApi } from '../Services/AiKeyApiProviderTypes';

function AiKeysHub() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getAiKeys, deleteAiKey } = useAiKeyApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [alertShown, setAlertShown] = useState<boolean>(false);
  const [aiKeys, setAiKeys] = useState<AiKeyApi[]>([]);
  const [totalAiKeys, setTotalAiKeys] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const [selectedRows, setSelectedRows] = useState<AiKeyApi[]>([]);
  const selectedId = selectedRows.length === 1 ? selectedRows[0].id : null;

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  const columns = [
    { name: 'Type', selector: (row: AiKeyApi) => row.type, sortable: false },
    { name: 'Model', selector: (row: AiKeyApi) => row.model, sortable: false },
    {
      name: 'Status',
      selector: (row: AiKeyApi) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
  ];

  const handleRowClicked = (row: AiKeyApi): void => {
    navigate(`/organizations/${organizationId}/ai-keys/${row.id}`, {
      state: { organizationId },
    });
  };

  const handleDelete = async (selectedId: string[]): Promise<void> => {
    if (!selectedId[0]) return;

    showConfirmation(`Are you sure you want to delete the selected item?`, async () => {
      try {
        setLoading(true);
        await deleteAiKey(selectedId[0]);
        await fetchAiKeys(0, itemsPerPage);
        showAlert('success', 'Item was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      } finally {
        setLoading(false);
      }
    });
  };

  const fetchAiKeys = async (skip: number, limit: number): Promise<void> => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getAiKeys(skip, limit);
      setAiKeys(response.items);
      setTotalAiKeys(response.totalCount);
    } catch (error) {
      setError((error as Error).message);
      showAlert('fail', 'Failed to fetch AI keys!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAiKeys(0, itemsPerPage);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="AI Keys"
                columns={columns}
                data={aiKeys}
                selectableRows
                selectableRowsHighlight
                paginationPerPage={itemsPerPage}
                progressPending={loading}
                paginationTotalRows={totalAiKeys}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                onChangePage={fetchAiKeys}
                getRowUrl={(row: AiKeyApi) => `/organizations/${organizationId}/ai-keys/${row.id}`}
                onSelectedRowsChange={({ selectedRows }: { selectedRows: AiKeyApi[] }) => {
                  setSelectedRows(selectedRows);
                }}
              />
              <Button
                background="color2"
                size="large"
                onClick={() => navigate(`/organizations/${organizationId}/ai-keys/new-ai-key`)}
              >
                Add AI Key
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AiKeysHubWrapper() {
  return (
    <ConfirmationProvider>
      <AiKeyApiProvider>
        <AiKeysHub />
      </AiKeyApiProvider>
    </ConfirmationProvider>
  );
}

export default AiKeysHubWrapper;
